import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 400;
const MARGIN = { top: 30, right: 100, bottom: 30, left: 100 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function strokeWidthScale(addressCount, allCounts) {
  if (!allCounts || allCounts.length === 0) return 2;
  const min = d3.min(allCounts);
  const max = d3.max(allCounts);
  if (min === max) return 2.5;
  const t = (addressCount - min) / (max - min);
  return 1.5 + t * 2.5; // 1.5px to 4px
}

export default function SlopeChart({ data, firstDate, lastDate }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [resizeCount, setResizeCount] = useState(0);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const rafId = requestAnimationFrame(() => {
      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();

      const containerWidth = containerRef.current
        ? containerRef.current.clientWidth
        : 700;
      const width = Math.max(500, containerWidth);
      const innerW = width - MARGIN.left - MARGIN.right;
      const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

      svg.attr('width', width).attr('height', CHART_HEIGHT);

      const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

      // Y scale: shared dollar scale across all first and last prices
      const allPrices = data.flatMap((d) => [d.firstPrice, d.lastPrice]);
      const priceMin = d3.min(allPrices);
      const priceMax = d3.max(allPrices);
      const pricePadding = (priceMax - priceMin) * 0.15 || 2;

      const y = d3.scaleLinear()
        .domain([priceMin - pricePadding, priceMax + pricePadding])
        .nice()
        .range([innerH, 0]);

      // Address counts for stroke width scaling
      const allCounts = data.map((d) => d.addressCount);

      // Grid lines
      const yTicks = y.ticks(8);
      g.selectAll('.grid-line')
        .data(yTicks)
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', (d) => y(d))
        .attr('y2', (d) => y(d))
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      // Y axis tick labels (centered)
      g.selectAll('.y-tick-label')
        .data(yTicks)
        .enter()
        .append('text')
        .attr('x', innerW / 2)
        .attr('y', (d) => y(d))
        .attr('dy', '-0.4em')
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('fill', '#d1d5db')
        .text((d) => `$${d.toFixed(0)}`);

      // Column headers
      g.append('text')
        .attr('x', 0)
        .attr('y', -12)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', 600)
        .attr('fill', '#6b7280')
        .text(firstDate || 'First');

      g.append('text')
        .attr('x', innerW)
        .attr('y', -12)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', 600)
        .attr('fill', '#6b7280')
        .text(lastDate || 'Last');

      // Vertical column lines
      g.append('line')
        .attr('x1', 0).attr('x2', 0)
        .attr('y1', 0).attr('y2', innerH)
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);

      g.append('line')
        .attr('x1', innerW).attr('x2', innerW)
        .attr('y1', 0).attr('y2', innerH)
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);

      // Draw slope lines and dots for each state
      const lineGroups = g.selectAll('.slope-group')
        .data(data)
        .enter()
        .append('g')
        .attr('class', 'slope-group');

      lineGroups.each(function (d) {
        const group = d3.select(this);
        const sw = strokeWidthScale(d.addressCount, allCounts);
        const change = d.changePercent;
        const lineColor = d.color || '#6b7280';

        let dashArray = 'none';
        if (change > 0) {
          dashArray = '6,3';
        } else if (change === 0) {
          dashArray = '2,3';
        }
        const actualLineColor = change === 0 ? '#9ca3af' : lineColor;

        // Slope line
        group.append('line')
          .attr('x1', 0)
          .attr('y1', y(d.firstPrice))
          .attr('x2', innerW)
          .attr('y2', y(d.lastPrice))
          .attr('stroke', actualLineColor)
          .attr('stroke-width', sw)
          .attr('stroke-dasharray', dashArray)
          .attr('opacity', 0.85)
          .style('cursor', 'pointer')
          .attr('data-state', d.state);

        // Left dot
        group.append('circle')
          .attr('cx', 0)
          .attr('cy', y(d.firstPrice))
          .attr('r', 5)
          .attr('fill', lineColor)
          .style('cursor', 'pointer');

        // Right dot
        group.append('circle')
          .attr('cx', innerW)
          .attr('cy', y(d.lastPrice))
          .attr('r', 5)
          .attr('fill', lineColor)
          .style('cursor', 'pointer');

        // Left price label
        group.append('text')
          .attr('x', -8)
          .attr('y', y(d.firstPrice))
          .attr('dy', '0.35em')
          .attr('text-anchor', 'end')
          .attr('font-size', '10px')
          .attr('fill', '#6b7280')
          .text(`$${d.firstPrice.toFixed(2)}`);

        // Right state + change label
        let labelColor = '#9ca3af';
        let changeStr = '0.0%';
        if (change > 0) {
          labelColor = '#16a34a';
          changeStr = `+${change.toFixed(1)}%`;
        } else if (change < 0) {
          labelColor = '#dc2626';
          changeStr = `${change.toFixed(1)}%`;
        }

        group.append('text')
          .attr('x', innerW + 8)
          .attr('y', y(d.lastPrice))
          .attr('dy', '0.35em')
          .attr('text-anchor', 'start')
          .attr('font-size', '11px')
          .attr('font-weight', 500)
          .attr('fill', labelColor)
          .text(`${d.state} ${changeStr}`);

        // Hover zone: invisible wide line for easier hovering
        group.append('line')
          .attr('x1', 0)
          .attr('y1', y(d.firstPrice))
          .attr('x2', innerW)
          .attr('y2', y(d.lastPrice))
          .attr('stroke', 'transparent')
          .attr('stroke-width', Math.max(sw + 8, 12))
          .style('cursor', 'pointer')
          .on('mouseenter', (event) => {
            // Dim all other groups
            g.selectAll('.slope-group').attr('opacity', 0.2);
            group.attr('opacity', 1);
            // Thicken this line
            group.select('line').attr('stroke-width', 4);

            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              state: d.state,
              firstPrice: d.firstPrice,
              lastPrice: d.lastPrice,
              changePercent: d.changePercent,
              addressCount: d.addressCount,
            });
          })
          .on('mousemove', (event) => {
            setTooltip((prev) => prev ? {
              ...prev,
              x: event.clientX + 12,
              y: event.clientY - 10,
            } : prev);
          })
          .on('mouseleave', () => {
            // Restore all groups
            g.selectAll('.slope-group').attr('opacity', 1);
            // Restore original stroke width
            const sw2 = strokeWidthScale(d.addressCount, allCounts);
            group.select('line').attr('stroke-width', sw2);
            setTooltip(null);
          });
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      d3.select(svgRef.current).selectAll('*').remove();
    };
  }, [data, firstDate, lastDate, resizeCount]);

  // Handle window resize — trigger redraw by bumping a counter
  useEffect(() => {
    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setResizeCount((c) => c + 1);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Movement by State</div>
        <div style={styles.subtitle}>First vs last scrape average comparison</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No slope comparison data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Movement by State</div>
      <div style={styles.subtitle}>First vs last scrape average comparison</div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>{firstDate || 'First'}</span>
            <span style={{ fontWeight: 600 }}>${tooltip.firstPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>{lastDate || 'Last'}</span>
            <span style={{ fontWeight: 600 }}>${tooltip.lastPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Change</span>
            <span style={{
              fontWeight: 600,
              color: tooltip.changePercent > 0 ? '#16a34a' : tooltip.changePercent < 0 ? '#dc2626' : '#9ca3af',
            }}>
              {tooltip.changePercent > 0 ? '+' : ''}{tooltip.changePercent.toFixed(1)}%
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600 }}>{tooltip.addressCount}</span>
          </div>
        </div>
      )}
    </div>
  );
}
